import { FC, useCallback, useMemo, useState } from 'react';
import { Center, Loader, Paper, Stack, Modal } from '@mantine/core';
import { DiagnosticReport } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useTranslation } from 'react-i18next';
import { usePatient } from '@hooks/usePatient';
import { ResourceSearchForm } from '@/components/resource-search/ResourceSearchForm';
import {
  ResourceTable,
  ResourceTableColumn,
} from '@/components/resource-search/ResourceTable';
import { SearchFieldDefinition } from '@/components/resource-search/ResourceSearchForm.types';
import { ReportFileModal } from './ReportFileModal';
import { ReportCard } from './components/ReportCard';
import DeleteConfirmModal from '../images-tab/DeleteModal';
import {
  getReportCategoryFromResource,
  getReportCategoryLabel,
  getReportResultValueFromCode,
  getReportResultLabel,
  deleteDiagnosticReport,
} from './utils/report-files.utils';
import { ARKON } from '@/consts/systems.consts';
import { formatDate } from '@/pages/ainigma/lims/utils/common-helpers';
import {
  getReportSearchFields,
  REPORT_SEARCH_DEFAULTS,
  ReportSearchParams,
} from './reportSearchConfig';
import { useReportSearch } from './useReportSearch';

export const ReportFileTab: FC = () => {
  const patient = usePatient();
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages', 'common']);

  const {
    resources: reports,
    loading,
    page,
    totalPages,
    setPage,
    searchParams,
    updateSearchParams,
    clearSearch,
    fetchResources,
  } = useReportSearch(medplum, patient?.id);
  const [reportModalOpened, setReportModalOpened] = useState(false);
  const [reportToEdit, setReportToEdit] = useState<DiagnosticReport | null>(
    null
  );
  const [reportCardModalOpened, setReportCardModalOpened] = useState(false);
  const [reportToView, setReportToView] = useState<DiagnosticReport | null>(
    null
  );
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<DiagnosticReport | null>(
    null
  );
  const [deleting, setDeleting] = useState(false);

  const handleSearch = useCallback(
    (updates: Partial<ReportSearchParams>) => {
      const mergedParams = { ...searchParams, ...updates };
      setPage(1);
      updateSearchParams(updates);
      fetchResources(mergedParams);
    },
    [searchParams, updateSearchParams, fetchResources, setPage]
  );

  const handleClear = useCallback(() => {
    const clearedParams: ReportSearchParams = { ...REPORT_SEARCH_DEFAULTS };
    setPage(1);
    clearSearch(clearedParams);
  }, [clearSearch, setPage]);

  const getReportCodeDisplay = useCallback(
    (report: DiagnosticReport): string => {
      const category = getReportCategoryFromResource(report);

      if (category === ARKON.CS.REPORTS.CATEGORY.CODES.GENETIC) {
        const geneticCoding = report.code?.coding?.find(
          (coding) => coding.system === ARKON.CS.GENETIC_TESTS.BASE
        );
        return (
          geneticCoding?.display ||
          geneticCoding?.code ||
          t('reportTable.noCode', { defaultValue: 'N/A' })
        );
      } else if (category === ARKON.CS.REPORTS.CATEGORY.CODES.INVESTIGATION) {
        const investigationCoding = report.code?.coding?.find(
          (coding) => coding.system === ARKON.CS.REPORTS.INVESTIGATION_TYPE.BASE
        );
        return (
          investigationCoding?.display ||
          investigationCoding?.code ||
          report.code?.text ||
          t('reportTable.noCode', { defaultValue: 'N/A' })
        );
      } else if (category === ARKON.CS.REPORTS.CATEGORY.CODES.BIOPSY) {
        return (
          report.code?.text || t('reportTable.noCode', { defaultValue: 'N/A' })
        );
      }

      return (
        report.code?.text ||
        report.code?.coding?.[0]?.display ||
        t('reportTable.noCode', { defaultValue: 'N/A' })
      );
    },
    [t]
  );

  const searchFields: SearchFieldDefinition<ReportSearchParams>[] = useMemo(
    () =>
      getReportSearchFields({
        t,
        searchParams,
        onSearch: handleSearch,
      }),
    [t, searchParams, handleSearch]
  );

  const columns: ResourceTableColumn<DiagnosticReport>[] = useMemo(
    () => [
      {
        key: 'category',
        label: t('reportTable.category', {
          ns: 'patient-pages',
          defaultValue: 'Κατηγορία',
        }),
        render: (report) => {
          const category = getReportCategoryFromResource(report);
          return t(
            `reportCards.categoryOptions.${getReportCategoryLabel(category)}`,
            {
              ns: 'patient-pages',
              defaultValue: getReportCategoryLabel(category),
            }
          );
        },
      },
      {
        key: 'code',
        label: t('reportTable.code', {
          ns: 'patient-pages',
          defaultValue: 'Κωδικός / Τύπος',
        }),
        render: (report) => getReportCodeDisplay(report),
      },
      {
        key: 'date',
        label: t('reportTable.date', {
          ns: 'patient-pages',
          defaultValue: 'Ημερομηνία',
        }),
        render: (report) =>
          report.effectiveDateTime
            ? formatDate(report.effectiveDateTime)
            : t('reportTable.noDate', { defaultValue: 'N/A' }),
      },
      {
        key: 'result',
        label: t('reportTable.result', {
          ns: 'patient-pages',
          defaultValue: 'Αποτέλεσμα',
        }),
        render: (report) => {
          const resultCode = report.conclusionCode?.[0]?.coding?.[0]?.code;
          const result = getReportResultValueFromCode(resultCode);
          const resultLabel = getReportResultLabel(result);
          return resultLabel
            ? t(`reportCards.resultOptions.${resultLabel}`, {
                ns: 'patient-pages',
                defaultValue: resultLabel,
              })
            : t('reportTable.noResult', { defaultValue: 'N/A' });
        },
      },
      {
        key: 'comments',
        label: t('reportTable.comments', {
          ns: 'patient-pages',
          defaultValue: 'Σχόλια',
        }),
        render: (report) =>
          report.conclusion
            ? report.conclusion.length > 50
              ? `${report.conclusion.substring(0, 50)}...`
              : report.conclusion
            : t('reportTable.noComments', { defaultValue: '-' }),
      },
    ],
    [t, getReportCodeDisplay]
  );

  const handleRowClick = useCallback(
    (report: DiagnosticReport) => {
      setReportToView(report);
      setReportCardModalOpened(true);
    },
    []
  );

  const handleActionClick = useCallback((report: DiagnosticReport) => {
    const attachmentUrl = report.presentedForm?.[0]?.url;
    if (attachmentUrl) {
      window.open(attachmentUrl, '_blank');
    }
  }, []);

  const handleReportModalClose = useCallback(() => {
    setReportModalOpened(false);
    setReportToEdit(null);
  }, []);

  const handleReportModalSuccess = useCallback(() => {
    fetchResources();
  }, [fetchResources]);

  const handleReportCardClose = useCallback(() => {
    setReportCardModalOpened(false);
    setReportToView(null);
  }, []);

  const handleEditFromCard = useCallback(
    (report: DiagnosticReport) => {
      setReportCardModalOpened(false);
      setReportToEdit(report);
      setReportModalOpened(true);
    },
    []
  );

  const handleDeleteFromCard = useCallback((report: DiagnosticReport) => {
    setReportToDelete(report);
    setDeleteModalOpened(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!reportToDelete) {
      return;
    }

    setDeleting(true);
    try {
      await deleteDiagnosticReport(medplum, reportToDelete);
      setDeleteModalOpened(false);
      setReportToDelete(null);
      if (reportCardModalOpened) {
        setReportCardModalOpened(false);
        setReportToView(null);
      }
      await fetchResources();
    } catch (error) {
      console.error('Failed to delete diagnostic report', error);
    } finally {
      setDeleting(false);
    }
  }, [medplum, reportToDelete, reportCardModalOpened, fetchResources]);

  const handleCloseDeleteModal = useCallback(() => {
    if (!deleting) {
      setDeleteModalOpened(false);
      setReportToDelete(null);
    }
  }, [deleting]);

  if (!patient) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Stack gap="md" className="p-6">
      <ResourceSearchForm<ReportSearchParams>
        fields={searchFields}
        searchParams={searchParams}
        onSearch={handleSearch}
        onClear={handleClear}
        clearLabel={t('reportTable.clear', {
          ns: 'patient-pages',
          defaultValue: 'Καθαρισμός',
        })}
        layout={{ cols: { base: 1, sm: 2, lg: 3 } }}
      />

      <Paper shadow="sm" withBorder>
        {loading ? (
          <Center p="xl">
            <Loader />
          </Center>
        ) : (
          <ResourceTable<DiagnosticReport>
            data={reports}
            columns={columns}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            onRowClick={handleRowClick}
            onActionClick={handleActionClick}
            emptyText={t('reportTable.empty', {
              ns: 'patient-pages',
              defaultValue: 'Δεν βρέθηκαν αναφορές',
            })}
          />
        )}
      </Paper>

      <Modal
        opened={reportCardModalOpened}
        onClose={handleReportCardClose}
        title={t('reportTable.viewReport', {
          ns: 'patient-pages',
          defaultValue: 'Προβολή αναφοράς',
        })}
        size="lg"
      >
        {reportToView && (
          <ReportCard
            report={reportToView}
            patient={patient}
            onEdit={handleEditFromCard}
            onDelete={handleDeleteFromCard}
          />
        )}
      </Modal>

      <ReportFileModal
        opened={reportModalOpened}
        onClose={handleReportModalClose}
        patient={patient}
        report={reportToEdit}
        onSuccess={handleReportModalSuccess}
      />

      <DeleteConfirmModal
        opened={deleteModalOpened}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        loading={deleting}
        message={t('reportCards.deleteConfirm', {
          ns: 'patient-pages',
          defaultValue:
            'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την αναφορά;',
        })}
      />
    </Stack>
  );
};
